import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, DollarSign, Briefcase, CalendarClock, Check, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import JobApplicationForm from "@/components/site/JobApplicationForm";
import { COMPANY } from "@/lib/constants";
import api from "@/lib/api";

const money = (n) => `$${n}`;

function List({ title, items, icon: Icon }) {
  if (!items?.length) return null;
  return (
    <div>
      <h2 className="font-heading text-2xl font-bold mb-4">{title}</h2>
      <ul className="space-y-2.5">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-3">
            <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <span className="text-muted-foreground leading-relaxed">{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function CareerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${slug}`)
      .then((r) => setJob(r.data))
      .catch(() => navigate("/careers", { replace: true }))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return <div className="py-40 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!job) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : `https://www.cgconcretellc.com`;
  const canonical = `${origin}/careers/${slug}`;
  const payText = `${money(job.salary_min)}–${money(job.salary_max)} / hour`;

  // Build a single HTML description containing everything visible on the page —
  // keeps the JobPosting schema fully consistent with page content (Google policy).
  const descHtml = [
    ...(job.intro || []).map((p) => `<p>${p}</p>`),
    `<p><strong>Schedule:</strong> ${job.schedule || ""}</p>`,
    job.responsibilities?.length ? `<p><strong>Responsibilities:</strong></p><ul>${job.responsibilities.map((r) => `<li>${r}</li>`).join("")}</ul>` : "",
    job.requirements?.length ? `<p><strong>Qualifications:</strong></p><ul>${job.requirements.map((r) => `<li>${r}</li>`).join("")}</ul>` : "",
    job.preferred?.length ? `<p><strong>Preferred:</strong></p><ul>${job.preferred.map((r) => `<li>${r}</li>`).join("")}</ul>` : "",
    job.physical?.length ? `<p><strong>Physical requirements:</strong></p><ul>${job.physical.map((r) => `<li>${r}</li>`).join("")}</ul>` : "",
    job.benefits?.length ? `<p><strong>Benefits:</strong></p><ul>${job.benefits.map((r) => `<li>${r}</li>`).join("")}</ul>` : "",
  ].filter(Boolean).join("");

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: descHtml,
      identifier: { "@type": "PropertyValue", name: job.identifier_name || COMPANY.name, value: job.slug },
      datePosted: job.date_posted,
      validThrough: job.valid_through,
      employmentType: job.schema_employment_type || ["FULL_TIME"],
      hiringOrganization: {
        "@type": "Organization",
        name: COMPANY.name,
        sameAs: origin,
        logo: `${origin}/archive/logo.jpg`,
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: job.location_city,
          addressRegion: job.location_state,
          postalCode: job.postal_code,
          addressCountry: "US",
        },
      },
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: job.salary_currency || "USD",
        value: { "@type": "QuantitativeValue", minValue: job.salary_min, maxValue: job.salary_max, unitText: job.salary_unit || "HOUR" },
      },
      applicantLocationRequirements: { "@type": "Country", name: "USA" },
      directApply: true,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Careers", item: origin + "/careers" },
        { "@type": "ListItem", position: 3, name: job.title, item: canonical },
      ],
    },
  ];

  const facts = [
    { icon: MapPin, label: "Location", value: `${job.location_city}, ${job.location_state}` },
    { icon: Briefcase, label: "Type", value: job.employment_type },
    { icon: DollarSign, label: "Pay", value: payText },
    { icon: CalendarClock, label: "Start", value: "Immediate" },
  ];

  return (
    <div>
      <SEO
        title={`${job.title} Job in ${job.location_city}, ${job.location_state} | CG Concrete LLC`}
        description={`Now hiring a ${job.title} in ${job.location_city}, ${job.location_state}. ${payText}. ${job.employment_type}. Apply online with CG Concrete LLC.`}
        canonical={canonical}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative bg-secondary text-white grain-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Link to="/careers" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-primary transition-colors mb-6" data-testid="career-back">
            <ArrowLeft className="h-4 w-4" /> All openings
          </Link>
          <span className="text-primary font-bold text-sm tracking-widest">NOW HIRING</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3">{job.title}</h1>
          <p className="text-white/75 mt-4 text-lg max-w-2xl">{job.tagline}</p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
            {facts.map((f) => (
              <div key={f.label} className="bg-white/5 border border-white/10 p-4 rounded-sm" data-testid={`career-fact-${f.label.toLowerCase()}`}>
                <f.icon className="h-5 w-5 text-primary mb-2" />
                <div className="text-[11px] uppercase tracking-wider text-white/50 font-bold">{f.label}</div>
                <div className="font-semibold text-sm mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <Button asChild className="h-12 px-8 bg-primary hover:bg-primary/90 rounded-sm font-bold">
              <a href="#apply" data-testid="career-apply-cta">Apply Now</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <div className="space-y-4">
            {job.intro?.map((p, i) => <p key={i} className="text-muted-foreground leading-relaxed text-lg">{p}</p>)}
          </div>

          <div className="bg-primary/5 border-l-4 border-primary p-6 flex flex-wrap gap-x-10 gap-y-3">
            <div><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Compensation</div><div className="font-heading text-xl font-bold">{payText}</div></div>
            <div><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Employment</div><div className="font-heading text-xl font-bold">{job.employment_type}</div></div>
            <div><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Location</div><div className="font-heading text-xl font-bold">{job.location_city}, {job.location_state}</div></div>
          </div>

          {job.schedule && (
            <div>
              <h2 className="font-heading text-2xl font-bold mb-3 flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> Schedule</h2>
              <p className="text-muted-foreground leading-relaxed">{job.schedule}</p>
            </div>
          )}

          <List title="What You'll Do" items={job.responsibilities} icon={Check} />
          <List title="Qualifications" items={job.requirements} icon={Check} />
          <List title="Preferred Experience" items={job.preferred} icon={Check} />
          <List title="Physical Requirements" items={job.physical} icon={Check} />
          <List title="Benefits & Perks" items={job.benefits} icon={Check} />

          {job.start_date && (
            <div className="bg-secondary text-white p-6 grain-texture">
              <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Start Date</h2>
              <p className="text-white/85 mt-2">{job.start_date}</p>
            </div>
          )}

          <div id="apply" className="scroll-mt-24 pt-4">
            <JobApplicationForm slug={job.slug} jobTitle={job.title} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <div className="bg-card border border-border p-6">
              <h3 className="font-heading text-lg font-bold">Ready to apply?</h3>
              <p className="text-sm text-muted-foreground mt-1">Fill out the quick application — it takes just a few minutes.</p>
              <Button asChild className="mt-4 w-full bg-primary hover:bg-primary/90 rounded-sm font-bold">
                <a href="#apply">Apply for this role</a>
              </Button>
              <a href={`tel:${COMPANY.phoneRaw}`} className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold hover:text-primary transition-colors">
                <Phone className="h-4 w-4 text-primary" /> Questions? {COMPANY.phone}
              </a>
            </div>
            <div className="bg-secondary text-white p-6">
              <h3 className="font-heading font-bold">Why CG Concrete?</h3>
              <ul className="mt-3 space-y-2 text-sm text-white/80">
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Locally owned, family-style crew</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Home every night — local work</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> Room to learn and advance</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
